import { Theme } from "../types";
import debugx from "debugx";

const debug = debugx("bikedash:theme");

type ThemeChangeCallback = (
  theme: Theme,
  resolvedTheme: "light" | "dark",
) => void;

class ThemeService {
  private currentTheme: Theme = "auto";
  private resolvedTheme: "light" | "dark" = "dark";
  private callbacks: Set<ThemeChangeCallback> = new Set();
  private mediaQuery: MediaQueryList | null = null;

  constructor() {
    // Check if we're in a browser environment
    if (typeof window !== "undefined") {
      this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      this.init();
    }
  }

  private init() {
    // Detect initial system theme
    this.resolvedTheme = this.detectSystemTheme();

    // Listen for system theme changes
    if (this.mediaQuery) {
      this.mediaQuery.addEventListener("change", this.handleSystemThemeChange);
    }

    debug("Theme service initialized, resolved theme: %s", this.resolvedTheme);
  }

  /**
   * Detect the system's preferred color scheme
   */
  private detectSystemTheme(): "light" | "dark" {
    if (this.mediaQuery?.matches) {
      return "dark";
    }
    return "light";
  }

  /**
   * Handle system theme changes (for auto mode)
   */
  private handleSystemThemeChange = () => {
    if (this.currentTheme === "auto") {
      const newResolvedTheme = this.detectSystemTheme();
      debug(
        "System theme changed to: %s (current mode: auto)",
        newResolvedTheme,
      );

      if (newResolvedTheme !== this.resolvedTheme) {
        this.resolvedTheme = newResolvedTheme;
        this.applyTheme(this.currentTheme);
        this.notifyCallbacks();
      }
    }
  };

  /**
   * Apply a theme to the document
   */
  applyTheme(theme: Theme) {
    this.currentTheme = theme;

    // Resolve the actual theme to apply
    let themeToApply: string;
    if (theme === "auto") {
      this.resolvedTheme = this.detectSystemTheme();
      themeToApply = this.resolvedTheme;
    } else if (
      theme === "high-contrast-light" ||
      theme === "high-contrast-dark"
    ) {
      themeToApply = theme;
      this.resolvedTheme = theme.includes("light") ? "light" : "dark";
    } else {
      themeToApply = theme;
      this.resolvedTheme = theme as "light" | "dark";
    }

    debug(
      "Applying theme: %s (resolved: %s, class: theme-%s)",
      theme,
      this.resolvedTheme,
      themeToApply,
    );

    // Remove all theme classes
    const html = document.documentElement;
    html.classList.remove(
      "theme-light",
      "theme-dark",
      "theme-high-contrast-light",
      "theme-high-contrast-dark",
    );

    // Add the new theme class
    html.classList.add(`theme-${themeToApply}`);

    // Update meta theme-color for PWA
    this.updateMetaThemeColor(themeToApply);

    this.notifyCallbacks();
  }

  /**
   * Update the meta theme-color tag for PWA/browser chrome
   */
  private updateMetaThemeColor(theme: string) {
    let themeColor = "#000000"; // Default dark

    switch (theme) {
      case "light":
        themeColor = "#ffffff";
        break;
      case "dark":
        themeColor = "#000000";
        break;
      case "high-contrast-light":
        themeColor = "#ffffff";
        break;
      case "high-contrast-dark":
        themeColor = "#000000";
        break;
    }

    // Update or create meta tag
    let metaTag = document.querySelector('meta[name="theme-color"]');
    if (!metaTag) {
      metaTag = document.createElement("meta");
      metaTag.setAttribute("name", "theme-color");
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute("content", themeColor);
  }

  /**
   * Get the current theme setting
   */
  getCurrentTheme(): Theme {
    return this.currentTheme;
  }

  /**
   * Get the resolved theme (light or dark)
   * This is useful for components that need to know the actual theme being displayed
   */
  getResolvedTheme(): "light" | "dark" {
    return this.resolvedTheme;
  }

  /**
   * Subscribe to theme changes
   */
  subscribe(callback: ThemeChangeCallback): () => void {
    this.callbacks.add(callback);
    debug("Theme subscriber added, total: %d", this.callbacks.size);

    // Immediately call with current theme
    callback(this.currentTheme, this.resolvedTheme);

    // Return unsubscribe function
    return () => {
      this.callbacks.delete(callback);
      debug("Theme subscriber removed, total: %d", this.callbacks.size);
    };
  }

  /**
   * Notify all subscribers of theme change
   */
  private notifyCallbacks() {
    this.callbacks.forEach((callback) => {
      callback(this.currentTheme, this.resolvedTheme);
    });
  }

  /**
   * Clean up event listeners
   */
  destroy() {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener(
        "change",
        this.handleSystemThemeChange,
      );
    }
    this.callbacks.clear();
    debug("Theme service destroyed");
  }
}

// Export singleton instance
export const themeService = new ThemeService();
